/**
 * FreeAgent Sync Integration
 * 
 * Contains status mapping, preflight checks, idempotency, audit logging,
 * and persistence of FreeAgent URLs to JARVIS.
 * 
 * This is the ONLY place that knows JARVIS statuses and DB persistence.
 */

import { isFreeAgentSyncEnabled } from '../config/freeagentConfig';
import * as freeAgentApi from '../freeagent/api';
import { deductionAmount, isDeductionLine } from '../modules/docs/utils/invoiceLedger';
import { toFreeAgentInvoiceAppUrl } from '../freeagent/appUrl';
import { directSupabase } from '../lib/supabaseClient';
import type { Document, DocumentWithLines, DocumentLine } from '../modules/docs/types/documents.types';
import {
  dateOnlyToIssuedAt,
  freeAgentInvoiceDatePayload,
  invoicePaymentScheduleLocked,
  resolveInvoicePaymentSchedule,
} from '../modules/docs/utils/invoicePaymentTerms';

export type SyncStatus = 'unsynced' | 'synced' | 'error';

export interface SyncResult {
  success: boolean;
  freeAgentUrl?: string;
  error?: string;
  wasNew?: boolean; // true if contact/invoice was newly created
  validationErrors?: string[]; // List of validation errors if any
  skipped?: boolean; // true when sync was intentionally not attempted
}

export const FREEAGENT_ALLOWED_ORG_CODE = 'KSG';
export const FREEAGENT_ALLOWED_ORG_NAME = 'Keystone Glazing';
/** KSU (Keystone South UK) shares Keystone Glazing's VAT registration and FreeAgent account. */
export const FREEAGENT_ALLOWED_ORG_CODES = [FREEAGENT_ALLOWED_ORG_CODE, 'KSU'];

type OrganisationSyncInfo = {
  id: string;
  name: string;
  code: string;
  freeagent_sync_enabled: boolean;
};

export interface FreeAgentOrgEligibility {
  eligible: boolean;
  organisationName?: string;
  organisationCode?: string;
  reason?: string;
}

function isKeystoneGlazingOrg(org?: Pick<OrganisationSyncInfo, 'code' | 'name'> | null): boolean {
  return FREEAGENT_ALLOWED_ORG_CODES.includes(String(org?.code || '').trim().toUpperCase());
}

/**
 * Why an organisation's invoices cannot go to FreeAgent, or null when they can.
 * Each brand's FreeAgent switch in Admin → Organisations decides. The KSG/KSU code list is
 * only used when that switch was not loaded.
 */
export function getFreeAgentOrganisationBlockReason(
  org?: (Pick<OrganisationSyncInfo, 'code' | 'name'> & { freeagent_sync_enabled?: boolean | null }) | null
): string | null {
  if (org && typeof org.freeagent_sync_enabled === 'boolean') {
    if (org.freeagent_sync_enabled) return null;
    const label = String(org.name || '').trim() || 'This organisation';
    return `FreeAgent is switched off for ${label}. Turn it on in Admin → Organisations, or move the invoice to a brand that sends to FreeAgent.`;
  }
  if (isKeystoneGlazingOrg(org)) {
    return null;
  }

  if (!org) {
    return `FreeAgent only accepts Keystone Glazing and Keystone South UK invoices. This invoice has no organisation assigned. Change the organisation to ${FREEAGENT_ALLOWED_ORG_NAME} before transmitting.`;
  }

  const name = String(org.name || '').trim() || 'another organisation';
  const code = String(org.code || '').trim();
  const codeLabel = code ? ` (${code})` : '';
  return `FreeAgent only accepts Keystone Glazing and Keystone South UK invoices. This invoice belongs to ${name}${codeLabel}. Change the organisation to ${FREEAGENT_ALLOWED_ORG_NAME} before transmitting.`;
}

async function getOrganisationSyncInfo(
  organisationId: string | null | undefined
): Promise<OrganisationSyncInfo | null> {
  if (!organisationId) {
    return null;
  }

  const { data } = await directSupabase
    .from('organisations')
    .select('id, name, code, freeagent_sync_enabled')
    .eq('id', organisationId)
    .maybeSingle();

  return (data as OrganisationSyncInfo | null) || null;
}

export async function getFreeAgentOrgEligibility(
  organisationId?: string | null
): Promise<FreeAgentOrgEligibility> {
  const org = await getOrganisationSyncInfo(organisationId);
  const reason = getFreeAgentOrganisationBlockReason(org);
  if (reason) {
    return {
      eligible: false,
      organisationName: org?.name,
      organisationCode: org?.code,
      reason,
    };
  }

  return {
    eligible: true,
    organisationName: org?.name,
    organisationCode: org?.code,
  };
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Map JARVIS document status to FreeAgent invoice status
 */
function mapJarvisStatusToFreeAgent(jarvisStatus: string): 'Draft' | 'Open' | 'Paid' | 'Cancelled' {
  switch (jarvisStatus) {
    case 'draft':
      return 'Draft';
    case 'sent':
      return 'Open';
    case 'paid':
      return 'Paid';
    case 'rejected':
      return 'Cancelled';
    default:
      return 'Draft';
  }
}

/**
 * Validation gates - block sync with explicit messages
 */
export async function validateInvoiceForSync(document: DocumentWithLines): Promise<ValidationResult> {
  const errors: string[] = [];

  // Check if sync is globally enabled (config level)
  if (!isFreeAgentSyncEnabled()) {
    errors.push('FreeAgent sync is not enabled globally');
    return { valid: false, errors };
  }

  // Only sync invoices
  if (document.kind !== 'Invoice') {
    errors.push('Only invoices can be synced to FreeAgent');
    return { valid: false, errors };
  }

  const org = await getOrganisationSyncInfo(document.organisationId);
  const orgBlockReason = getFreeAgentOrganisationBlockReason(org);
  if (orgBlockReason) {
    errors.push(orgBlockReason);
    return { valid: false, errors };
  }

  // Check if organisation has FreeAgent sync enabled (org-level master switch)
  if (!org?.freeagent_sync_enabled) {
    errors.push('FreeAgent sync is not enabled for this organisation');
    return { valid: false, errors };
  }

  const accountId = (document as { accountId?: string | null }).accountId;
  const vatTreatment = (document as { vatTreatment?: string | null }).vatTreatment;
  if (vatTreatment === 'outside_scope') {
    errors.push('FreeAgent invoice lines can only be taxable or exempt. Record this outside-scope invoice in FreeAgent directly.');
  }

  // Check the invoice is addressed to a customer or a directory account
  if (accountId) {
    const { data: account } = await directSupabase
      .from('directory_accounts')
      .select('id, company_name')
      .eq('id', accountId)
      .maybeSingle();
    if (!account) {
      errors.push('Account not found');
    } else if (!String(account.company_name || '').trim()) {
      errors.push('Account must have a company name to sync to FreeAgent');
    }
  } else if (!document.customerId) {
    errors.push('Invoice must have a customer to sync to FreeAgent');
  } else {
    // Check if customer can be created/retrieved
    const { data: customer } = await directSupabase
      .from('customers')
      .select('id, name, email, phone_number, address')
      .eq('id', document.customerId)
      .single();

    if (!customer) {
      errors.push('Customer not found');
    } else {
      // Validate customer has minimum required data for FreeAgent
      if (!customer.name || customer.name.trim().length === 0) {
        errors.push('Customer must have a name to sync to FreeAgent');
      }
      // Note: FreeAgent doesn't strictly require email/phone, but we'll warn if missing
    }
  }

  // Check if invoice has lines
  if (!document.lines || document.lines.length === 0) {
    errors.push('Invoice must have at least one line item to sync to FreeAgent');
  } else {
    // Validate each line
    document.lines.forEach((line, index) => {
      const hasName = typeof line.name === 'string' && line.name.trim().length > 0;
      const hasDescription = typeof line.description === 'string' && line.description.trim().length > 0;

      // FreeAgent sync currently sends a single aggregated totals line item
      // (see convertDocumentTotalsToFreeAgentItems), so source line descriptions
      // are not required for sync validity.
      if (!hasName && !hasDescription) {
        console.warn(`FreeAgent sync validation: line ${index + 1} has no name/description; allowing due to totals-based sync`, {
          documentId: document.id,
          lineId: line.id,
        });
      }
      if (line.qty !== undefined && line.qty < 0) {
        errors.push(`Line ${index + 1}: Quantity cannot be negative`);
      }
      // A reissued balance's agreed deductions are negative lines; the invoice total must still be positive
      if (line.unitPrice !== undefined && line.unitPrice < 0 && !isDeductionLine(line)) {
        errors.push(`Line ${index + 1}: Unit price cannot be negative`);
      }
    });
  }

  // Check currency (must be GBP for FreeAgent)
  if (!document.currency || document.currency.trim().length === 0) {
    errors.push('Invoice must have a currency');
  } else if (document.currency.toUpperCase() !== 'GBP') {
    errors.push('FreeAgent sync only supports GBP currency');
  }

  // Check totals are valid (must be >= 0)
  if (document.totals) {
    if (document.totals.grandTotal < 0) {
      errors.push('Invoice total cannot be negative');
    }
    // Check individual line totals
    document.lines.forEach((line, index) => {
      const lineTotal = line.lineTotal != null ? line.lineTotal : (line.qty || 0) * (line.unitPrice || 0);
      if (lineTotal < 0 && !isDeductionLine(line)) {
        errors.push(`Line ${index + 1}: Line total cannot be negative`);
  }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get or create FreeAgent contact for a JARVIS customer
 * Returns the contact URL and whether it was newly created
 */
async function ensureFreeAgentContact(customerId: string): Promise<{ contactUrl: string; wasNew: boolean }> {
  // Validate customerId is provided
  if (!customerId) {
    throw new Error('Customer ID is required for FreeAgent contact lookup');
  }

  console.log('🔍 ensureFreeAgentContact called with customerId:', customerId);

  // Check if customer already has a FreeAgent contact URL stored
  const { data: customer, error: customerError } = await directSupabase
    .from('customers')
    .select('id, first_name, last_name, email, address, billing_address, freeagent_contact_url')
    .eq('id', customerId)
    .maybeSingle(); // Use maybeSingle() to handle case where customer doesn't exist

  if (customerError) {
    console.error('❌ Error fetching customer:', {
      customerId,
      error: customerError.message,
      errorCode: customerError.code,
    });
    throw new Error(`Failed to fetch customer ${customerId}: ${customerError.message}`);
  }

  if (!customer) {
    console.error('❌ Customer not found:', { customerId });
    throw new Error(`Customer ${customerId} not found`);
  }

  // Debug: Log the customer being used
  console.log('👤 FreeAgent contact lookup:', {
    customerId,
    customerFirstName: customer.first_name,
    customerLastName: customer.last_name,
    customerEmail: customer.email,
    customerAddress: customer.address,
    customerBillingAddress: customer.billing_address,
    hasFreeAgentContactUrl: !!customer.freeagent_contact_url,
    freeAgentContactUrl: customer.freeagent_contact_url,
  });

  // If contact URL already exists, verify it still matches this customer
  if (customer.freeagent_contact_url) {
    console.log('✅ Found stored FreeAgent contact URL, verifying it matches customer:', {
      storedContactUrl: customer.freeagent_contact_url,
      customerId,
      customerFirstName: customer.first_name,
      customerLastName: customer.last_name,
      customerEmail: customer.email,
    });
    
    // Verify the stored contact URL matches this customer by fetching it from FreeAgent
    try {
      const freeAgentContact = await freeAgentApi.getContact(customer.freeagent_contact_url);
      
      // Check if the contact matches this customer
      const emailMatches = freeAgentContact.email?.toLowerCase() === customer.email?.toLowerCase();
      const firstNameMatches = freeAgentContact.first_name?.toLowerCase() === customer.first_name?.toLowerCase();
      const lastNameMatches = freeAgentContact.last_name?.toLowerCase() === customer.last_name?.toLowerCase();
      
      if (emailMatches && firstNameMatches && lastNameMatches) {
        console.log('✅ Stored FreeAgent contact URL matches customer:', {
          contactUrl: customer.freeagent_contact_url,
          freeAgentContact: {
            firstName: freeAgentContact.first_name,
            lastName: freeAgentContact.last_name,
            email: freeAgentContact.email,
          },
        });
    return { contactUrl: customer.freeagent_contact_url, wasNew: false };
      } else {
        console.warn('⚠️ Stored FreeAgent contact URL does NOT match customer! Creating new contact:', {
          storedContactUrl: customer.freeagent_contact_url,
          freeAgentContact: {
            firstName: freeAgentContact.first_name,
            lastName: freeAgentContact.last_name,
            email: freeAgentContact.email,
          },
          expectedCustomer: {
            firstName: customer.first_name,
            lastName: customer.last_name,
            email: customer.email,
          },
          matches: {
            email: emailMatches,
            firstName: firstNameMatches,
            lastName: lastNameMatches,
          },
        });
        // Clear the incorrect URL and create a new contact
        await directSupabase
          .from('customers')
          .update({ freeagent_contact_url: null })
          .eq('id', customerId);
        // Continue to create new contact below
      }
    } catch (error: any) {
      // If we can't fetch the contact (404, etc.), it might not exist anymore
      console.warn('⚠️ Could not verify stored FreeAgent contact URL, creating new contact:', {
        storedContactUrl: customer.freeagent_contact_url,
        error: error?.message,
      });
      // Clear the invalid URL and create a new contact
      await directSupabase
        .from('customers')
        .update({ freeagent_contact_url: null })
        .eq('id', customerId);
      // Continue to create new contact below
    }
  }

  // Validate customer has minimum required data
  const firstName = customer.first_name;
  const lastName = customer.last_name;
  
  if (!firstName || !lastName) {
    throw new Error('Customer must have first_name and last_name to create FreeAgent contact');
  }

  if (!customer.email) {
    throw new Error('Customer must have an email to create FreeAgent contact');
  }

  // Create contact in FreeAgent (matches FreeAgent API spec)
  console.log('🆕 Creating new FreeAgent contact:', {
    firstName,
    lastName,
    email: customer.email,
    address: customer.address,
    billingAddress: customer.billing_address,
  });
  
  const contactUrl = await freeAgentApi.ensureContact({
    firstName,
    lastName,
    email: customer.email,
    address: customer.address,
    billingAddress: customer.billing_address,
  });

  console.log('✅ Created FreeAgent contact:', contactUrl);

  // Store the contact URL on the customer record for future use
  const { error: updateError } = await directSupabase
    .from('customers')
    .update({ freeagent_contact_url: contactUrl })
    .eq('id', customerId);

  if (updateError) {
    console.error('❌ Failed to store FreeAgent contact URL:', updateError);
    // Don't throw - the contact was created, we just couldn't store the URL
  }

  return { contactUrl, wasNew: true };
}

/**
 * Get or create the FreeAgent contact for a directory account (a business, not a person).
 * The stored URL is reused while FreeAgent still has that contact under the same organisation name.
 */
export async function ensureFreeAgentAccountContact(accountId: string): Promise<{ contactUrl: string; wasNew: boolean }> {
  const { data: account, error } = await directSupabase
    .from('directory_accounts')
    .select('id, company_name, contact_name, email, billing_email, address, vat_registered, vat_number, freeagent_contact_url')
    .eq('id', accountId)
    .maybeSingle();
  if (error) throw new Error(`Failed to fetch account ${accountId}: ${error.message}`);
  if (!account) throw new Error(`Account ${accountId} not found`);

  const companyName = String(account.company_name || '').trim();
  if (account.freeagent_contact_url) {
    try {
      const existing = await freeAgentApi.getContact(account.freeagent_contact_url);
      if (String(existing.organisation_name || '').trim().toLowerCase() === companyName.toLowerCase()) {
        return { contactUrl: account.freeagent_contact_url, wasNew: false };
      }
    } catch (verifyError: any) {
      console.warn('Stored FreeAgent contact for account could not be verified, resolving again:', {
        accountId,
        error: verifyError?.message,
      });
    }
  }

  const contactUrl = await freeAgentApi.ensureOrganisationContact({
    organisationName: companyName,
    contactName: account.contact_name,
    email: account.billing_email || account.email,
    address: account.address,
    vatNumber: account.vat_registered ? account.vat_number : null,
  });
  const { error: updateError } = await directSupabase
    .from('directory_accounts')
    .update({ freeagent_contact_url: contactUrl })
    .eq('id', accountId);
  if (updateError) {
    console.error('Failed to store FreeAgent contact URL on account:', updateError);
  }
  return { contactUrl, wasNew: contactUrl !== account.freeagent_contact_url };
}

/** FreeAgent invoice-level VAT status for a document's VAT treatment. */
export function freeAgentEcStatusFor(vatTreatment?: string | null): freeAgentApi.FreeAgentEcStatus | undefined {
  return vatTreatment === 'reverse_charge' ? 'Reverse Charge' : undefined;
}

/**
 * Convert JARVIS document totals to FreeAgent invoice items
 * Uses stored database fields: net_total, vat_amount, gross_total
 * 
 * For FreeAgent, we send the net total as the line item price, and FreeAgent calculates VAT and gross.
 * Matches FreeAgent API spec: { description, item_type, price, quantity, sales_tax_rate }
 * 
 * Field rules:
 * - item_type ∈ "Services" | "Products" | "Hours" | "Days"
 * - price = unit net (must be >= 0) - this is the net total (subtotal before VAT)
 * - quantity = number (must be > 0)
 * - sales_tax_rate = percentage number (e.g., 20, 5, 0)
 */
function convertDocumentTotalsToFreeAgentItems(document: DocumentWithLines): freeAgentApi.FreeAgentInvoiceItem[] {
  // Get stored totals from database (passed via docData)
  // These are stored fields: net_total, vat_amount, gross_total
  let netTotal = Number((document as any).net_total ?? 0);
  const vatAmount = Number((document as any).vat_amount ?? 0);
  const grossTotal = Number((document as any).gross_total ?? 0);

  // If net_total is missing or zero, try to calculate it from gross_total and vat_amount
  if (netTotal <= 0 && grossTotal > 0) {
    if (vatAmount > 0) {
      // netTotal = grossTotal - vatAmount
      netTotal = grossTotal - vatAmount;
      console.log('⚠️ net_total was missing/zero in convertDocumentTotalsToFreeAgentItems, calculated from gross_total and vat_amount:', {
        calculatedNetTotal: netTotal,
        grossTotal,
        vatAmount,
      });
    } else {
      // No VAT, so netTotal = grossTotal
      netTotal = grossTotal;
      console.log('⚠️ net_total was missing/zero in convertDocumentTotalsToFreeAgentItems, using gross_total as net_total:', {
        calculatedNetTotal: netTotal,
        grossTotal,
      });
    }
  }

  // Validate net total
  if (netTotal <= 0) {
    console.error('❌ Cannot sync invoice to FreeAgent - net total is zero or negative:', {
      netTotal,
      vatAmount,
      grossTotal,
      documentId: document.id,
      lifecycleRef: document.lifecycleRef,
    });
    throw new Error(`Invoice net total must be greater than 0. Current values: net_total=${netTotal}, gross_total=${grossTotal}, vat_amount=${vatAmount}. Please ensure the invoice has line items and totals have been calculated.`);
  }

  // FreeAgent accepts specific sales tax rates in most UK orgs.
  // Prefer the configured VAT pct from the document, then normalize
  // to common supported rates to avoid 422 validation errors.
  let vatRatePercent = 0;
  if (document.vatEnabled) {
    if (document.vatPct != null && Number.isFinite(Number(document.vatPct))) {
      vatRatePercent = Math.round(Number(document.vatPct) * 100);
    } else if (vatAmount > 0 && netTotal > 0) {
      const calculatedRate = vatAmount / netTotal;
      vatRatePercent = Math.round(calculatedRate * 100);
    }
  }
  const normalizeVatRate = (rate: number): number => {
    const n = Number(rate);
    if (!Number.isFinite(n)) return 0;
    if (n <= 0) return 0;
    const supported = [0, 5, 20];
    return supported.reduce((best, candidate) =>
      Math.abs(candidate - n) < Math.abs(best - n) ? candidate : best
    , supported[0]);
  };
  vatRatePercent = normalizeVatRate(vatRatePercent);

  // Debug logging
  console.log('Converting document totals to FreeAgent items:', {
    netTotal,
    vatAmount,
    grossTotal,
    vatEnabled: document.vatEnabled,
    vatPct: document.vatPct,
    vatRatePercent,
    calculatedRate: vatAmount > 0 && netTotal > 0 ? (vatAmount / netTotal) : null,
    expectedVatFromNet: netTotal * (vatRatePercent / 100),
  });

  // Single line item representing the invoice total
  // FreeAgent calculates: net_value = price * quantity, sales_tax_value = net_value * (sales_tax_rate / 100), total_value = net_value + sales_tax_value
  // We send the net total as price, and FreeAgent should calculate VAT and gross total
  // FreeAgent gets one line for the total, so name any deductions in its description
  const deductions = (document.lines || []).filter((line) => isDeductionLine(line));
  const deductionNote = deductions.length
    ? ` (after ${deductions.map((line) => `${line.name || 'deduction'} £${deductionAmount(line).toFixed(2)}`).join(', ')})`
    : '';
  const item = {
    description: `${document.title || 'Invoice Total'}${deductionNote}`,
    item_type: 'Services' as const,
    price: netTotal, // Stored net_total (subtotal before VAT) - FreeAgent will calculate VAT = price * (sales_tax_rate / 100)
    quantity: 1,
    sales_tax_rate: vatRatePercent, // VAT rate as percentage (e.g., 20 for 20%) - FreeAgent calculates VAT from this
    ...((document as { vatTreatment?: string | null }).vatTreatment === 'exempt'
      ? { sales_tax_status: 'EXEMPT' as const }
      : {}),
  };

  console.log('FreeAgent invoice item:', item);

  return [item];
}

/**
 * Log sync action to audit table
 */
async function logSyncAction(
  documentId: string,
  action: 'create' | 'update' | 'error' | 'resync',
  freeAgentUrl?: string,
  error?: string,
  metadata?: Record<string, any>,
  userId?: string
): Promise<void> {
  try {
    await directSupabase
      .from('freeagent_sync_audit_log')
      .insert({
        document_id: documentId,
        action,
        freeagent_url: freeAgentUrl || null,
        error_message: error || null,
        performed_by: userId || null,
        metadata: metadata || null,
      });
  } catch (err) {
    // Don't fail the sync if audit logging fails
    console.error('Failed to log sync action:', err);
  }
}

/**
 * Handle invoice status change trigger
 * Called when invoice status changes to sent|paid|rejected
 * 
 * Logic:
 * - If orgSync = OFF → no-op for all invoices
 * - If orgSync = ON:
 *   - When status = sent: create invoice (if URL empty) then mark Sent, or ensure Sent (idempotent)
 *   - When status = paid: mark Paid in FA
 *   - When status = rejected: mark Cancelled in FA
 */
export async function handleInvoiceStatusChange(
  documentId: string,
  newStatus: string,
  oldStatus?: string,
  userId?: string,
  isRecreating?: boolean, // Guard against infinite recursion
  isManual?: boolean
): Promise<SyncResult> {
  // Only handle sent, paid, or rejected
  if (!['sent', 'paid', 'rejected'].includes(newStatus)) {
    return { success: true }; // No-op for other statuses
  }

  try {
    // Fetch full document with lines and stored totals
    const { data: docData, error: docError } = await directSupabase
      .from('documents')
      .select(`
        *,
        document_lines (*)
      `)
      .eq('id', documentId)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist

    if (docError) {
      console.error('❌ Error fetching document for FreeAgent sync:', {
        documentId,
        error: docError.message,
        errorCode: docError.code,
      });
      throw new Error(`Failed to fetch document ${documentId}: ${docError.message}`);
    }

    if (!docData) {
      throw new Error(`Document ${documentId} not found`);
    }

    // Debug: Log the document data being used
    console.log('📄 FreeAgent sync - document data:', {
      documentId,
      lifecycleRef: docData.lifecycle_ref,
      customerId: docData.customer_id,
      customerFirstName: docData.customer_first_name,
      customerLastName: docData.customer_last_name,
      customerEmail: docData.customer_email,
      title: docData.title,
    });

    // Convert to DocumentWithLines format
    // Map database fields to Document format (organisation_id -> organisationId, etc.)
    const mappedLines: DocumentLine[] = (docData.document_lines || []).map((line: any) => ({
      id: line.id,
      documentId: line.document_id,
      lineKind: line.line_kind,
      materialId: line.material_id,
      equipmentId: line.equipment_id,
      labourRateId: line.labour_rate_id,
      name: line.name,
      description: line.description,
      qty: Number(line.qty ?? 0),
      unit: line.unit,
      unitPrice: Number(line.unit_price ?? 0),
      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
      vatInclusive: !!line.vat_inclusive,
      sortOrder: line.sort_order,
      labourDays: line.labour_days,
      labourHours: line.labour_hours,
      // Carries the deduction flag on a reissued balance's negative lines
      metaJson: line.meta_json ?? null,
      createdAt: line.created_at,
      updatedAt: line.updated_at,
    }));

    // Use stored totals from database (net_total, vat_amount, gross_total)
    // These are stored fields, not computed
    let netTotal = Number(docData.net_total ?? 0);
    let vatAmount = Number(docData.vat_amount ?? 0);
    let grossTotal = Number(docData.gross_total ?? 0);
    
    // If net_total is missing or zero, try to calculate it from gross_total and vat_amount
    // This handles cases where totals weren't calculated/stored yet
    if (netTotal <= 0 && grossTotal > 0) {
      if (vatAmount > 0) {
        // netTotal = grossTotal - vatAmount
        netTotal = grossTotal - vatAmount;
        console.log('⚠️ net_total was missing/zero, calculated from gross_total and vat_amount:', {
          calculatedNetTotal: netTotal,
          grossTotal,
          vatAmount,
        });
      } else {
        // No VAT, so netTotal = grossTotal
        netTotal = grossTotal;
        console.log('⚠️ net_total was missing/zero, using gross_total as net_total:', {
          calculatedNetTotal: netTotal,
          grossTotal,
        });
      }
    }
    
    // Determine VAT enabled: check explicit flag first, then infer from vat_amount
    let vatEnabled: boolean;
    if (docData.vat_enabled !== undefined && docData.vat_enabled !== null) {
      vatEnabled = !!docData.vat_enabled;
    } else if (docData.vatEnabled !== undefined && docData.vatEnabled !== null) {
      vatEnabled = !!docData.vatEnabled;
    } else {
      // Infer from vat_amount: if there's VAT, it's enabled
      vatEnabled = vatAmount > 0;
    }
    
    // Determine VAT percentage: check explicit value first, then calculate from amounts
    let vatPct: number;
    if (docData.vat_pct !== undefined && docData.vat_pct !== null) {
      vatPct = Number(docData.vat_pct);
    } else if (docData.vatPct !== undefined && docData.vatPct !== null) {
      vatPct = Number(docData.vatPct);
    } else if (vatEnabled && netTotal > 0) {
      // Calculate from stored amounts: vatPct = vatAmount / netTotal
      vatPct = vatAmount / netTotal;
    } else {
      // Default to 20% (0.2) for UK
      vatPct = 0.2;
    }
    
    const document: DocumentWithLines = {
      ...docData,
      organisationId: docData.organisation_id || docData.organisationId, // Handle both formats
      lifecycleRef: docData.lifecycle_ref || docData.lifecycleRef,
      invoiceRef: docData.invoice_ref || docData.invoiceRef || null,
      projectId: docData.project_id || docData.projectId,
      customerId: docData.customer_id || docData.customerId,
      accountId: docData.account_id || docData.accountId || null,
      vatTreatment: docData.vat_treatment || docData.vatTreatment || null,
      quotedockCustomerId: docData.quotedock_customer_id || docData.quotedockCustomerId,
      customerFirstName: docData.customer_first_name || docData.customerFirstName,
      customerLastName: docData.customer_last_name || docData.customerLastName,
      customerTitle: docData.customer_title || docData.customerTitle,
      issuedAt: docData.issued_at || docData.issuedAt || null, // Map issued_at to issuedAt
      createdAt: docData.created_at || docData.createdAt || null,
      invoiceRole: docData.invoice_role || docData.invoiceRole || null,
      paymentTermsInDays: docData.payment_terms_in_days != null
        ? Number(docData.payment_terms_in_days)
        : (docData.paymentTermsInDays != null ? Number(docData.paymentTermsInDays) : null),
      freeagentInvoiceUrl: docData.freeagent_invoice_url || null,
      vatEnabled,
      vatPct,
      lines: mappedLines,
      totals: {
        // Create a minimal totals object with the stored values
        // We only need preProfit + profit for net total, and grandTotal
        materialsTotal: 0,
        equipmentTotal: 0,
        labourTotal: 0,
        otherTotal: 0,
        baseCost: 0,
        adminFee: 0,
        contingency: 0,
        developer: 0,
        preProfit: netTotal, // Use calculated netTotal (may have been recalculated from gross_total)
        profit: 0, // profit = 0 since we're using net_total directly
        grandTotal: grossTotal,
      } as any,
    };
    
    // Store the calculated netTotal in the document object for use in convertDocumentTotalsToFreeAgentItems
    (document as any).net_total = netTotal;
    (document as any).vat_amount = vatAmount;
    (document as any).gross_total = grossTotal;

    // Only sync invoices
    if (document.kind !== 'Invoice') {
      return { success: true }; // No-op for non-invoices
    }

    // Guard: require organisation ID
    if (!document.organisationId) {
      throw new Error('Document must have an organisation ID to sync to FreeAgent');
    }

    const org = await getOrganisationSyncInfo(document.organisationId);
    const orgBlockReason = getFreeAgentOrganisationBlockReason(org);
    if (orgBlockReason || !org?.freeagent_sync_enabled) {
      const errors = orgBlockReason
        ? [orgBlockReason]
        : ['FreeAgent sync is not enabled for this organisation'];

      // Automatic status changes for Property/Security invoices should not look like a
      // successful FreeAgent send, but also should not stamp those invoices as sync errors.
      if (!isManual) {
        console.warn('Skipping FreeAgent sync:', {
          documentId,
          organisationId: document.organisationId,
          organisationName: org?.name,
          organisationCode: org?.code,
          reason: errors[0],
        });
        return {
          success: false,
          skipped: true,
          error: errors[0],
          validationErrors: errors,
        };
      }

      await directSupabase
        .from('documents')
        .update({
          sync_status: 'error',
          last_synced_at: new Date().toISOString(),
          sync_error: errors.join('; '),
        })
        .eq('id', documentId);

      await logSyncAction(
        documentId,
        'error',
        undefined,
        errors.join('; '),
        { oldStatus, newStatus, organisationName: org?.name, organisationCode: org?.code },
        userId
      );

      return {
        success: false,
        error: errors[0],
        validationErrors: errors,
      };
    }

    // Validate before proceeding (customer, lines, etc.)
    const validation = await validateInvoiceForSync(document);
    if (!validation.valid) {
      // Update sync status to error
      await directSupabase
        .from('documents')
        .update({
          sync_status: 'error',
          last_synced_at: new Date().toISOString(),
          sync_error: validation.errors.join('; '),
        })
        .eq('id', documentId);

      // Log validation error
      await logSyncAction(
        documentId,
        'error',
        undefined,
        validation.errors.join('; '),
        { oldStatus, newStatus },
        userId
      );

      return {
        success: false,
        error: validation.errors.join('; '),
        validationErrors: validation.errors,
      };
    }

    // Get or create FreeAgent contact
    // Debug: Log the customer ID being used - verify it matches the document
    console.log('🔍 FreeAgent sync - customer lookup:', {
      documentId,
      customerIdFromDocument: document.customerId,
      customerIdFromDocData: docData.customer_id,
      customerFirstName: document.customerFirstName,
      customerLastName: document.customerLastName,
      lifecycleRef: document.lifecycleRef,
      title: document.title,
    });
    
    const syncAccountId = document.accountId || null;
    if (!syncAccountId && !document.customerId) {
      console.error('❌ Document missing customer ID:', {
        documentId,
        docDataCustomerId: docData.customer_id,
        documentCustomerId: document.customerId,
      });
      throw new Error('Document must have a customer ID to sync to FreeAgent');
    }
    
    // Double-check: verify the customerId matches what's in the database
    // Use docData.customer_id as the source of truth
    const finalCustomerId = docData.customer_id || document.customerId;
    
    if (!syncAccountId && !finalCustomerId) {
      console.error('❌ No customer ID found in document:', {
        documentId,
        docDataCustomerId: docData.customer_id,
        documentCustomerId: document.customerId,
      });
      throw new Error('Document must have a customer ID to sync to FreeAgent');
    }
    
    if (docData.customer_id && docData.customer_id !== document.customerId) {
      console.error('⚠️ Customer ID mismatch! Using docData value:', {
        documentId,
        docDataCustomerId: docData.customer_id,
        documentCustomerId: document.customerId,
      });
    }
    
    console.log('✅ Using customer ID for FreeAgent sync:', {
      documentId,
      customerId: finalCustomerId,
    });
    
    const { contactUrl } = syncAccountId
      ? await ensureFreeAgentAccountContact(syncAccountId)
      : await ensureFreeAgentContact(finalCustomerId);

    // Get current FA invoice URL
    const { data: currentDoc, error: currentDocError } = await directSupabase
      .from('documents')
      .select('freeagent_invoice_url')
      .eq('id', documentId)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist

    if (currentDocError) {
      console.error('❌ Error fetching current document for FreeAgent URL:', {
        documentId,
        error: currentDocError.message,
      });
      throw new Error(`Failed to fetch document ${documentId}: ${currentDocError.message}`);
    }

    let freeAgentInvoiceUrl: string;
    let invoiceWasNew = false;

    if (newStatus === 'sent') {
      if (!currentDoc?.freeagent_invoice_url) {
        // Create new invoice in FreeAgent as Draft
        // Use totals breakdown instead of individual line items
        const invoiceItems = convertDocumentTotalsToFreeAgentItems(document);
        // Capture stored totals for debugging/logging
        let logNetTotal = Number((document as any).net_total ?? 0);
        let logVatAmount = Number((document as any).vat_amount ?? 0);
        let logGrossTotal = Number((document as any).gross_total ?? 0);
        if (logNetTotal <= 0 && logGrossTotal > 0) {
          if (logVatAmount > 0) {
            logNetTotal = logGrossTotal - logVatAmount;
          } else {
            logNetTotal = logGrossTotal;
          }
        }
        console.log('🧾 FreeAgent totals debug:', {
          documentId,
          lifecycleRef: document.lifecycleRef,
          netTotal: logNetTotal,
          vatAmount: logVatAmount,
          grossTotal: logGrossTotal,
          vatEnabled: document.vatEnabled,
          vatPct: document.vatPct,
          items: invoiceItems,
        });
        
        const locked = invoicePaymentScheduleLocked({
          freeagentInvoiceUrl: (document as { freeagentInvoiceUrl?: string | null }).freeagentInvoiceUrl,
        });
        const schedule = resolveInvoicePaymentSchedule({
          invoiceRole: document.invoiceRole,
          issuedAt: document.issuedAt,
          createdAt: document.createdAt,
          paymentTermsInDays: document.paymentTermsInDays,
          locked,
        });
        const { datedOn, dueOn, paymentTermsInDays } = freeAgentInvoiceDatePayload(schedule);
        if (!locked) {
          const persistIssuedAt = dateOnlyToIssuedAt(schedule.invoiceDate);
          const { error: datePersistError } = await directSupabase
            .from('documents')
            .update({
              issued_at: persistIssuedAt,
              payment_terms_in_days: schedule.paymentTermsInDays,
            })
            .eq('id', documentId);
          if (datePersistError && !/payment_terms_in_days|schema cache/i.test(datePersistError.message)) {
            throw new Error(`Failed to persist invoice dates: ${datePersistError.message}`);
          }
          document.issuedAt = persistIssuedAt;
          document.paymentTermsInDays = schedule.paymentTermsInDays;
        }
        console.log('📅 FreeAgent invoice dates:', {
          datedOn,
          dueOn,
          paymentTermsInDays,
          invoiceRole: document.invoiceRole,
        });

        // Validate currency is GBP
        // Field mapping: invoice.currency → invoice.currency (must be GBP)
        const currency = (document.currency || 'GBP').toUpperCase();
        if (currency !== 'GBP') {
          throw new Error('FreeAgent sync only supports GBP currency');
        }

        // Get stored totals to explicitly set them in FreeAgent
        const netTotal = Number((document as any).net_total ?? 0);
        const vatAmount = Number((document as any).vat_amount ?? 0);
        const grossTotal = Number((document as any).gross_total ?? 0);
        
        freeAgentInvoiceUrl = await freeAgentApi.createInvoiceDraft({
          contactUrl, // customer.freeagent_contact_url → invoice.contact (URL)
          datedOn,
          dueOn,
          paymentTermsInDays,
          reference: document.invoiceRef || document.lifecycleRef, // JARVIS invoice ref → FreeAgent invoice.reference
          currency: 'GBP', // invoice.currency → invoice.currency (enforced GBP)
          items: invoiceItems, // See convertDocumentLinesToFreeAgentItems for line mappings
          netValue: netTotal, // Explicitly set net value
          involvesSalesTax: vatAmount > 0, // Set sales tax flag based on whether VAT exists
          salesTaxValue: vatAmount, // Explicitly set VAT amount
          totalValue: grossTotal, // Explicitly set gross total
          ecStatus: freeAgentEcStatusFor(document.vatTreatment),
        });

        invoiceWasNew = true;

        // Try to update the invoice with explicit totals after creation
        // FreeAgent may calculate from line items and ignore explicit values on creation
        // Skip update if we're recreating to avoid infinite loops
        // Note: FreeAgent may not allow updating certain fields on Draft invoices,
        // so we'll transition to Sent first, then update if needed
        if (!isRecreating) {
          try {
            // First, transition to Sent (this may be required before updating totals)
        await freeAgentApi.markInvoiceSent(freeAgentInvoiceUrl);
            
            // Small delay to ensure FreeAgent has processed the transition
            await new Promise(resolve => setTimeout(resolve, 500));

            console.log('🔄 Updating FreeAgent invoice with explicit totals:', {
              invoiceUrl: freeAgentInvoiceUrl,
              netTotal,
              vatAmount,
              grossTotal,
              involvesSalesTax: vatAmount > 0,
            });

            // Update the invoice with explicit totals to ensure correct display
            await freeAgentApi.updateInvoice(freeAgentInvoiceUrl, {
              netValue: netTotal,
              involvesSalesTax: vatAmount > 0,
              salesTaxValue: vatAmount,
              totalValue: grossTotal,
            });

            // Small delay to ensure FreeAgent has processed the update
            await new Promise(resolve => setTimeout(resolve, 500));

            // Verify the invoice was updated correctly
            const updatedInvoice = await freeAgentApi.getInvoice(freeAgentInvoiceUrl);
            const expectedNet = Number((document as any).net_total ?? 0);
            const expectedVat = Number((document as any).vat_amount ?? 0);
            const expectedGross = Number((document as any).gross_total ?? 0);
            
          } catch (verifyError: any) {
            console.warn('⚠️ Failed to update/verify FreeAgent invoice totals (invoice was still created):', {
              error: verifyError?.message,
              status: verifyError?.status,
              details: verifyError?.details,
            });
          }
        } else {
          // If we're recreating, just transition to Sent (skip the update step)
          await freeAgentApi.markInvoiceSent(freeAgentInvoiceUrl);
        }

        // Store FA URL and set sync_status = synced
        await directSupabase
          .from('documents')
          .update({
            freeagent_invoice_url: freeAgentInvoiceUrl,
            sync_status: 'synced',
            last_synced_at: new Date().toISOString(),
            sync_error: null,
          })
          .eq('id', documentId);

        await logSyncAction(
          documentId,
          'create',
          freeAgentInvoiceUrl,
          undefined,
          { oldStatus, newStatus, action: 'create_and_send' },
          userId
        );
      } else {
        // Invoice URL exists - try to mark as sent
        freeAgentInvoiceUrl = currentDoc.freeagent_invoice_url;
        
        try {
        await freeAgentApi.markInvoiceSent(freeAgentInvoiceUrl);
        
        await directSupabase
          .from('documents')
          .update({
            sync_status: 'synced',
            last_synced_at: new Date().toISOString(),
            sync_error: null,
          })
          .eq('id', documentId);

        await logSyncAction(
          documentId,
          'update',
          freeAgentInvoiceUrl,
          undefined,
          { oldStatus, newStatus, action: 'mark_sent' },
          userId
        );
        } catch (error: any) {
          // If invoice doesn't exist (404), recreate it (but only once to prevent infinite loops)
          // Only recreate when FreeAgent says the invoice itself is gone, never when it
          // merely refuses a transition; recreating then would duplicate the invoice.
          const is404 =
            error.status === 404 ||
            error.message?.includes('Invoice not found in FreeAgent');
          
          if (!isRecreating && is404) {
            console.warn(`Invoice ${freeAgentInvoiceUrl} not found in FreeAgent, recreating...`);
            
            // Clear the stored URL and recreate
            await directSupabase
              .from('documents')
              .update({ freeagent_invoice_url: null })
              .eq('id', documentId);
            
            // Recreate invoice (recursive call with isRecreating flag to prevent infinite loops)
            return handleInvoiceStatusChange(documentId, newStatus, oldStatus, userId, true, isManual);
          }
          // Re-throw other errors
          throw error;
        }
      }

    } else if (newStatus === 'paid') {
      if (currentDoc?.freeagent_invoice_url) {
        freeAgentInvoiceUrl = currentDoc.freeagent_invoice_url;
      } else if (!isManual) {
        // Paid before it was sent to FreeAgent is fine; it goes over as paid when someone sends it.
        return { success: true, skipped: true };
      } else {
        // Marked paid in JARVIS before it was ever sent: create and issue it in FreeAgent first.
        const created = await handleInvoiceStatusChange(documentId, 'sent', oldStatus, userId, false, isManual);
        if (!created.success) return created;
        if (!created.freeAgentUrl) {
          throw new Error('Cannot mark invoice as paid: FreeAgent did not return the new invoice');
        }
        freeAgentInvoiceUrl = created.freeAgentUrl;
        invoiceWasNew = true;
      }
      await freeAgentApi.markInvoicePaid(freeAgentInvoiceUrl);

      await directSupabase
        .from('documents')
        .update({
          sync_status: 'synced',
          last_synced_at: new Date().toISOString(),
          sync_error: null,
        })
        .eq('id', documentId);

      await logSyncAction(
        documentId,
        'update',
        freeAgentInvoiceUrl,
        undefined,
        { oldStatus, newStatus, action: 'mark_paid' },
        userId
      );

    } else if (newStatus === 'rejected') {
      if (!currentDoc?.freeagent_invoice_url) {
        throw new Error('Cannot mark invoice as rejected: invoice not yet synced to FreeAgent');
      }

      freeAgentInvoiceUrl = currentDoc.freeagent_invoice_url;
      await freeAgentApi.markInvoiceCancelled(freeAgentInvoiceUrl);

      await directSupabase
        .from('documents')
        .update({
          sync_status: 'synced',
          last_synced_at: new Date().toISOString(),
          sync_error: null,
        })
        .eq('id', documentId);

      await logSyncAction(
        documentId,
        'update',
        freeAgentInvoiceUrl,
        undefined,
        { oldStatus, newStatus, action: 'mark_cancelled' },
        userId
      );
    }

    return {
      success: true,
      freeAgentUrl: freeAgentInvoiceUrl!,
      wasNew: invoiceWasNew,
    };
  } catch (error: any) {
    // Any failure → set sync_status = error + capture sync_error
    await directSupabase
      .from('documents')
      .update({
        sync_status: 'error',
        last_synced_at: new Date().toISOString(),
        sync_error: error.message || 'Unknown error',
      })
      .eq('id', documentId);

    // Audit log error
    await logSyncAction(
      documentId,
      'error',
      undefined,
      error.message || 'Unknown error',
      { oldStatus, newStatus },
      userId
    );

    return {
      success: false,
      error: error.message || 'Unknown error',
    };
  }
}

/**
 * Manual resync - re-runs orchestration for an invoice
 * 
 * For manual sync, we treat draft invoices as "sent" so they get created and marked as sent in FreeAgent.
 * This allows users to manually sync draft invoices without having to change the status first.
 */
export async function resyncInvoiceToFreeAgent(
  documentId: string,
  userId?: string,
  options?: { source?: string }
): Promise<SyncResult> {
  // Fetch current document status
  const { data: docData, error: docError } = await directSupabase
    .from('documents')
    .select('status, kind, organisation_id')
    .eq('id', documentId)
    .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist

  if (docError) {
    console.error('Error fetching document for resync:', {
      documentId,
      error: docError.message,
    });
    throw new Error(`Failed to fetch document ${documentId}: ${docError.message}`);
  }

  if (!docData) {
    throw new Error(`Document ${documentId} not found`);
  }

  // Only allow manual sync for invoices
  if (docData.kind !== 'Invoice') {
    throw new Error('FreeAgent sync is only available for invoices');
  }

  const org = await getOrganisationSyncInfo(docData.organisation_id);
  const orgBlockReason = getFreeAgentOrganisationBlockReason(org);
  if (orgBlockReason) {
    return {
      success: false,
      error: orgBlockReason,
      validationErrors: [orgBlockReason],
    };
  }

  // Tell FreeAgent to create/mark the invoice issued. Do not write JARVIS status to
  // sent — Sent is reserved for customer transmissions.
  const syncStatus = docData.status === 'draft' ? 'sent' : docData.status;

  console.log('🔄 Manual FreeAgent sync triggered:', {
    documentId,
    currentStatus: docData.status,
    syncStatus,
    note: docData.status === 'draft'
      ? 'Issuing to FreeAgent without changing JARVIS customer-send status'
      : undefined,
  });

  const result = await handleInvoiceStatusChange(documentId, syncStatus, docData.status, userId, false, true);
  await logSyncAction(
    documentId,
    'resync',
    result.freeAgentUrl,
    result.error,
    {
      source: options?.source || 'manual_resync',
      jarvisStatus: docData.status,
      syncStatus,
    },
    userId
  );
  return result;
}

/**
 * Get sync status for a document
 */
export async function getSyncStatus(documentId: string): Promise<{
  syncStatus: SyncStatus;
  freeAgentUrl?: string;
  lastSyncedAt?: string;
  syncError?: string;
}> {
  const { data, error } = await directSupabase
    .from('documents')
    .select('sync_status, freeagent_invoice_url, last_synced_at, sync_error')
    .eq('id', documentId)
    .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist

  if (error) {
    console.error('Error fetching sync status:', {
      documentId,
      error: error.message,
    });
    throw new Error(`Failed to fetch document ${documentId}: ${error.message}`);
  }

  if (!data) {
    throw new Error(`Document ${documentId} not found`);
  }

  return {
    syncStatus: (data.sync_status as SyncStatus) || 'unsynced',
    freeAgentUrl: toFreeAgentInvoiceAppUrl(data.freeagent_invoice_url),
    lastSyncedAt: data.last_synced_at || undefined,
    syncError: data.sync_error || undefined,
  };
}

/**
 * Get audit log for a document
 */
export async function getSyncAuditLog(documentId: string): Promise<Array<{
  id: string;
  action: string;
  freeagentUrl?: string;
  errorMessage?: string;
  performedBy?: string;
  createdAt: string;
  metadata?: any;
}>> {
  const { data, error } = await directSupabase
    .from('freeagent_sync_audit_log')
    .select('*')
    .eq('document_id', documentId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch audit log:', error);
    return [];
  }

  return (data || []).map(log => ({
    id: log.id,
    action: log.action,
    freeagentUrl: toFreeAgentInvoiceAppUrl(log.freeagent_url),
    errorMessage: log.error_message || undefined,
    performedBy: log.performed_by || undefined,
    createdAt: log.created_at,
    metadata: log.metadata || undefined,
  }));
}
